import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PentagonOutlinedIcon from '@mui/icons-material/PentagonOutlined'
import { Box, Button, GlobalStyles, IconButton, Tooltip } from '@mui/material'
import {
  Autocomplete,
  GoogleMap,
  Libraries,
  MarkerF,
  PolygonF,
  useJsApiLoader,
} from '@react-google-maps/api'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { TerraDraw, TerraDrawPolygonMode } from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'
import { IGeofence } from '../../features/Shipments/types'
import SpinnerBlock from '../Spinner/SpinnerBlock'
import { drawPolygonStyles, polygonOptions } from './const'
import messages from './messages'
import {
  DrawingToolbar,
  MapTypeToggle,
  SearchBoxContainer,
  SearchInput,
  SearchInputWrapper,
  Wrapper,
} from './styles'
import { PolygonMapProps } from './types'

// Module-level so useJsApiLoader gets a stable array: a new array per render
// makes it reload the Maps script. The 'drawing' library is gone from the
// Maps JavaScript API (unavailable since May 2026); Terra Draw replaces it.
const LIBRARIES: Libraries = ['places']

// Terra Draw set up to draw one polygon at a time. The finished shape is
// removed from Terra Draw and handed to onDrawn as the open {lat, lng} ring
// the forms store, the same value the old DrawingManager produced.
const createPolygonDraw = (map: google.maps.Map, onDrawn: (polygon: IGeofence[]) => void) => {
  const draw = new TerraDraw({
    adapter: new TerraDrawGoogleMapsAdapter({
      lib: google.maps,
      map,
      coordinatePrecision: 9,
      isolatedData: true,
    }),
    modes: [new TerraDrawPolygonMode({ styles: drawPolygonStyles })],
  })
  draw.on('finish', (id, { mode, action }) => {
    if (mode !== 'polygon' || action !== 'draw') {
      return
    }
    const feature = draw.getSnapshotFeature(id)
    draw.removeFeatures([id])
    draw.setMode('static')
    if (feature?.geometry.type === 'Polygon') {
      // GeoJSON rings are [lng, lat] and repeat the first point at the end.
      const ring: number[][] = feature.geometry.coordinates[0].slice(0, -1)
      onDrawn(ring.map(([lng, lat]) => ({ lat, lng })))
    }
  })
  return draw
}

const PolygonMap = ({
  polygons = [],
  defaultCenter,
  marker,
  onChangePolygons,
  onChangeMarker,
  handleGeocode,
  googleMapsApiKey,
  height = 600,
  width = '100%',
  drawingControl = true,
  draggable = true,
  editable = true,
}: PolygonMapProps) => {
  const { formatMessage } = useIntl()
  // The initial view only. Do not clear it after load: a null center leaves
  // map.getBounds() null, and Terra Draw needs the bounds to place a vertex.
  const [center] = useState(defaultCenter)
  const mapRef = useRef<google.maps.Map>()
  const [map, setMap] = useState<google.maps.Map>()
  const polygonRef = useRef<any>([])
  const drawRef = useRef<TerraDraw>()
  const onChangePolygonsRef = useRef(onChangePolygons)
  onChangePolygonsRef.current = onChangePolygons
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [mapTypeId, setMapTypeId] = useState<'roadmap' | 'satellite'>('roadmap')
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey, libraries: LIBRARIES })
  const mapOptions: google.maps.MapOptions = {
    mapTypeControl: false,
    panControl: false,
    rotateControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: false,
    scaleControl: false,
    keyboardShortcuts: false,
  }
  const [geoCoder, setGeocoder] = useState<google.maps.Geocoder>()

  const canDraw = editable && drawingControl
  const showToolbar = canDraw && polygons.length === 0

  const onLoadMap = (map: google.maps.Map) => {
    mapRef.current = map
    setMap(map)
  }

  const setMapType = (type: 'roadmap' | 'satellite') => {
    setMapTypeId(type)
    mapRef.current?.setMapTypeId(type)
  }

  const onLoadPolygon = (polygon: google.maps.Polygon) => {
    polygonRef.current = polygon
    centerMap(polygon)
  }

  const centerMap = (polygon: google.maps.Polygon) => {
    if (mapRef.current) {
      google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
        const bounds = new google.maps.LatLngBounds()
        polygon.getPath().forEach((e) => bounds.extend(e))
        mapRef.current?.fitBounds(bounds)
      })
    }
  }

  const onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete
  }

  const onPlaceChanged = () => {
    const autocomplete = autocompleteRef.current
    const map = mapRef.current
    if (!autocomplete || !map) {
      return
    }

    const place = autocomplete.getPlace()
    if (!place.geometry || !place.geometry.location) {
      return
    }

    if (place.geometry.viewport) {
      map.fitBounds(place.geometry.viewport)
    } else {
      map.setCenter(place.geometry.location)
      map.setZoom(17)
    }
  }

  useEffect(() => {
    if (isLoaded) {
      const geoCoder = new google.maps.Geocoder()
      setGeocoder(geoCoder)
    }
  }, [isLoaded])

  // Terra Draw is used only to draw a new polygon. Once drawn, the shape is
  // handed to the form and shown by the PolygonF below, which keeps the
  // vertex editing and dragging it had with the old DrawingManager.
  useEffect(() => {
    if (!map || !canDraw) {
      return
    }
    let draw: TerraDraw | undefined
    const startDraw = () => {
      draw = createPolygonDraw(map, (polygon) => {
        setIsDrawingMode(false)
        onChangePolygonsRef.current(polygon)
      })
      draw.start()
      drawRef.current = draw
    }
    // The adapter needs the map's rendered DOM, which exists once the
    // projection is set (the pattern in Google's Terra Draw sample).
    let listener: google.maps.MapsEventListener | undefined
    if (map.getProjection()) {
      startDraw()
    } else {
      listener = google.maps.event.addListenerOnce(map, 'projection_changed', startDraw)
    }
    return () => {
      listener?.remove()
      draw?.stop()
      drawRef.current = undefined
      setIsDrawingMode(false)
    }
  }, [map, canDraw])

  const toggleDrawingMode = () => {
    const draw = drawRef.current
    if (!draw?.enabled) {
      return
    }
    const next = !isDrawingMode
    draw.setMode(next ? 'polygon' : 'static')
    setIsDrawingMode(next)
  }

  const clearPolygon = () => {
    onChangePolygons([])
  }

  const onEditPolygon = () => {
    const polygon = polygonRef.current
    if (polygon) {
      const coordinates = polygon
        .getPath()
        .getArray()
        .map((latLng: google.maps.LatLng) => ({ lat: latLng.lat(), lng: latLng.lng() }))
      onChangePolygons(coordinates)
    }
  }

  return (
    <Box display='flex' height={height} width={width} justifyContent='flex-end'>
      <GlobalStyles styles={{ '.pac-container': { zIndex: 1500 } }} />
      {isLoaded ? (
        <Wrapper>
          <GoogleMap
            zoom={center?.zoom || 6}
            center={center}
            options={mapOptions}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            onLoad={onLoadMap}
            onClick={(e) => {
              const lat = e.latLng?.lat()
              const lng = e.latLng?.lng()
              // A click while drawing places a polygon vertex, not the marker.
              if (editable && !isDrawingMode && lat && lng && onChangeMarker) {
                geoCoder?.geocode({ location: e.latLng }).then((res: any) => {
                  handleGeocode && handleGeocode(res.results[0])
                  onChangeMarker({
                    lat: lat,
                    lng: lng,
                  })
                })
              }
            }}
          >
            {marker && (
              <MarkerF
                position={{ lat: marker.lat, lng: marker.lng }}
                draggable={!!editable}
                onDragEnd={(e) => {
                  const lat = e.latLng?.lat()
                  const lng = e.latLng?.lng()

                  if (lat && lng && onChangeMarker) {
                    geoCoder?.geocode({ location: e.latLng }).then((res: any) => {
                      handleGeocode && handleGeocode(res.results[0])
                      onChangeMarker({
                        lat: lat,
                        lng: lng,
                      })
                    })
                  }
                }}
              />
            )}
            {polygons.length > 0 && (
              <PolygonF
                draggable={!!draggable && !!editable}
                editable={!!editable}
                options={{ ...polygonOptions, editable, draggable }}
                paths={polygons}
                onLoad={onLoadPolygon}
                onMouseUp={onEditPolygon}
                onDragEnd={onEditPolygon}
              />
            )}
          </GoogleMap>
          {editable && (
            <SearchBoxContainer>
              <MapTypeToggle>
                <Button
                  size='small'
                  onClick={() => setMapType('roadmap')}
                  disableElevation
                  variant={mapTypeId === 'roadmap' ? 'contained' : 'text'}
                  sx={{
                    minWidth: 0,
                    textTransform: 'none',
                    px: 1.5,
                    height: '34px',
                    color: mapTypeId === 'roadmap' ? 'primary.contrastText' : 'primary.main',
                  }}
                >
                  Map
                </Button>
                <Button
                  size='small'
                  onClick={() => setMapType('satellite')}
                  disableElevation
                  variant={mapTypeId === 'satellite' ? 'contained' : 'text'}
                  sx={{
                    minWidth: 0,
                    textTransform: 'none',
                    px: 1.5,
                    height: '34px',
                    color: mapTypeId === 'satellite' ? 'primary.contrastText' : 'primary.main',
                  }}
                >
                  Satellite
                </Button>
              </MapTypeToggle>
              <SearchInputWrapper>
                <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                  <SearchInput
                    type='text'
                    placeholder={formatMessage(messages.mapSearchBoxPlaceholder)}
                  />
                </Autocomplete>
              </SearchInputWrapper>
              {showToolbar && (
                <DrawingToolbar>
                  <Tooltip title={formatMessage(messages.drawPolygon)} arrow>
                    <IconButton
                      size='small'
                      onClick={toggleDrawingMode}
                      sx={{
                        color: isDrawingMode ? 'primary.contrastText' : 'primary.main',
                        backgroundColor: isDrawingMode ? 'primary.main' : 'transparent',
                        '&:hover': {
                          backgroundColor: isDrawingMode ? 'primary.main' : 'action.hover',
                        },
                        borderRadius: '4px',
                      }}
                    >
                      <PentagonOutlinedIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </DrawingToolbar>
              )}
              {editable && polygons.length > 0 && (
                <DrawingToolbar>
                  <Tooltip title={formatMessage(messages.clearPolygon)} arrow>
                    <IconButton
                      size='small'
                      onClick={clearPolygon}
                      sx={{ color: 'primary.main', borderRadius: '4px' }}
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </DrawingToolbar>
              )}
            </SearchBoxContainer>
          )}
        </Wrapper>
      ) : (
        <SpinnerBlock />
      )}
    </Box>
  )
}

export default PolygonMap
